import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Search, ClipboardCheck, Eye, FileText, Calendar, AlertTriangle } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const SystematicInspections = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const inspectionTypes = [
    {
      title: 'Visual Inspection',
      description: 'Comprehensive visual assessment of equipment surfaces, coatings, and structural integrity.',
      icon: Eye
    },
    {
      title: 'Non-Destructive Testing',
      description: 'Advanced techniques including ultrasonic testing, magnetic particle inspection, and dye penetrant testing.',
      icon: Search
    },
    {
      title: 'Thickness Measurements',
      description: 'Precise measurements to track wear rates and predict remaining service life.',
      icon: ClipboardCheck
    },
    {
      title: 'Documentation & Reporting',
      description: 'Detailed reports with photos, measurements, and actionable recommendations.',
      icon: FileText
    }
  ];

  const inspectionProcess = [
    { step: 1, title: 'Schedule Assessment', description: 'We work with your schedule to minimize operational impact.' },
    { step: 2, title: 'On-Site Inspection', description: 'Our certified technicians conduct thorough equipment assessments.' },
    { step: 3, title: 'Data Analysis', description: 'We analyze findings to identify trends and prioritize issues.' },
    { step: 4, title: 'Detailed Report', description: 'Receive a comprehensive report with recommendations and timelines.' },
    { step: 5, title: 'Action Plan', description: 'We develop a customized maintenance plan for your facility.' }
  ];

  const benefits = [
    'Early detection of potential failures',
    'Prioritized repair scheduling',
    'Compliance documentation',
    'Historical data tracking',
    'Budget planning support',
    'Reduced emergency repairs'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Systematic Inspections" description="Systematic inspection services for industrial assets. Identify corrosion, erosion, and wear before failure." path="/maintenance/systematic-inspections" />
      
      <PageHero
        badge="SMART Program"
        title="Systematic Inspections"
        description="Regular, thorough assessments of your equipment to identify issues before they become costly failures. Our systematic approach ensures nothing is overlooked."
        backgroundKey="systematic-inspections"
        buttonText="Schedule an Inspection"
      />

      {/* Inspection Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Inspection Methods</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {inspectionTypes.map((type, index) => {
              const IconComponent = type.icon;
              return (
                <Card key={index} className="border-l-4 border-l-blue-900">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <IconComponent className="text-blue-900 mr-3" size={28} />
                      {type.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{type.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Inspection Process</h2>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            {inspectionProcess.map((item, index) => (
              <div key={index} className="flex-1 text-center">
                <div className="w-12 h-12 bg-green-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
                {index < inspectionProcess.length - 1 && (
                  <div className="hidden md:block absolute top-6 right-0 w-full h-0.5 bg-green-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Benefits of Systematic Inspections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start p-4 bg-green-50 rounded-lg">
                <CheckCircle className="text-green-700 mr-3 mt-1 flex-shrink-0" size={20} />
                <p className="text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <AlertTriangle className="mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold mb-4">Don't Wait for Equipment Failure</h2>
          <p className="text-blue-100 mb-8">
            Proactive inspections cost a fraction of emergency repairs. Schedule your assessment today.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Request Inspection Quote <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SystematicInspections;
